import { useState, useEffect } from 'react';
import api from './api';
import OnboardingPage from './OnboardingPage';
import { useTheme } from './ThemeContext';

// ─── Country list ─────────────────────────────────────────────────────────────
const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia','Bosnia and Herzegovina',
  'Brazil','Bulgaria','Cambodia','Canada','Chile','China','Colombia','Croatia',
  'Czech Republic','Denmark','Ecuador','Egypt','Estonia','Ethiopia','Finland',
  'France','Georgia','Germany','Ghana','Greece','Guatemala','Hungary','India',
  'Indonesia','Iran','Iraq','Ireland','Israel','Italy','Japan','Jordan','Kazakhstan',
  'Kenya','Kuwait','Kyrgyzstan','Latvia','Lebanon','Libya','Lithuania','Luxembourg',
  'Malaysia','Mexico','Moldova','Mongolia','Montenegro','Morocco','Netherlands',
  'New Zealand','Nigeria','North Macedonia','Norway','Oman','Pakistan','Peru',
  'Philippines','Poland','Portugal','Qatar','Romania','Russia','Saudi Arabia',
  'Serbia','Singapore','Slovakia','Slovenia','South Africa','South Korea','Spain',
  'Sri Lanka','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Thailand',
  'Tunisia','Turkey','Turkmenistan','Ukraine','United Arab Emirates',
  'United Kingdom','United States','Uzbekistan','Venezuela','Vietnam','Yemen',
];

// ─── Input field ──────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: '6px', color: 'var(--text-primary)', padding: '10px 14px',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};

const inputDisabledStyle = {
  ...inputStyle, color: 'var(--text-dim)', cursor: 'not-allowed',
};

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  const color = type === 'error' ? 'var(--accent)' : 'var(--success-text)';
  const bg    = type === 'error' ? 'var(--danger-bg)'  : 'var(--success-bg)';
  return (
    <div style={{
      position: 'fixed', bottom: '32px', right: '32px',
      background: bg, border: `1px solid ${color}`, color,
      borderRadius: '8px', padding: '12px 20px', fontSize: '14px',
      zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    }}>
      {msg}
    </div>
  );
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────
export default function ProfilePage({ currentUser, onBack, onUserUpdated }) {
  const [tab, setTab] = useState('personal');
  const [toast, setToast] = useState({ msg: '', type: 'ok' });

  // ── Personal info state ───────────────────────────────────────────────────
  const [personal, setPersonal] = useState({
    name:       currentUser?.name       || '',
    phone:      currentUser?.phone      || '',
    country:    currentUser?.country    || '',
    city:       currentUser?.city       || '',
  });
  const [personalSaving, setPersonalSaving] = useState(false);

  // ── Password state ────────────────────────────────────────────────────────
  const [pwd, setPwd] = useState({ current: '', new: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  // ── Preferences: тема інтерфейсу (застосовується миттєво через setTheme,
  // окремої кнопки Save тут не потрібно — на відміну від валюти раніше) ─────
  const { theme, setTheme } = useTheme();

  // ── Full freelancer profile (тільки для ролі Freelancer) ─────────────────
  const isFreelancer = currentUser?.role === 'Freelancer';
  const [myFreelancer, setMyFreelancer] = useState(null);
  useEffect(() => {
    if (!isFreelancer) return;
    let cancelled = false;
    api.freelancers.me()
      .then(res => { if (!cancelled) setMyFreelancer(res.freelancer); })
      .catch(() => { if (!cancelled) setMyFreelancer(null); });
    return () => { cancelled = true; };
  }, [isFreelancer]);

  // Для фрілансера — синхронізувати Phone/Country/City з таблиці freelancers
  // (там реально зберігаються дані з онбордингу), а не з users.
  useEffect(() => {
    if (!isFreelancer || !myFreelancer) return;
    setPersonal(p => ({
      ...p,
      phone:   myFreelancer.phone             || p.phone,
      country: myFreelancer.address?.country  || p.country,
      city:    myFreelancer.address?.city     || p.city,
    }));
  }, [isFreelancer, myFreelancer]);

  // ─────────────────────────────────────────────────────────────────────────
  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'ok' }), 3500);
  };

  // ── Save personal ─────────────────────────────────────────────────────────
  const savePersonal = async () => {
    if (!personal.name.trim()) return showToast('Name is required', 'error');
    setPersonalSaving(true);
    try {
      const { user } = await api.users.update(currentUser.id, personal);
      onUserUpdated?.(user);
      // Для фрілансера — дублювати Phone/Country/City і в таблицю freelancers,
      // щоб дані лишались синхронними з "My Details & Documents".
      if (isFreelancer && myFreelancer) {
        const res = await api.freelancers.updateMe({
          phone: personal.phone,
          address: { ...myFreelancer.address, country: personal.country, city: personal.city },
        });
        setMyFreelancer(res.freelancer);
      }
      showToast('Profile updated successfully');
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setPersonalSaving(false);
    }
  };

  // ── Save password ─────────────────────────────────────────────────────────
  const savePassword = async () => {
    if (!pwd.current) return showToast('Enter your current password', 'error');
    if (pwd.new.length < 8) return showToast('New password must be at least 8 characters', 'error');
    if (pwd.new !== pwd.confirm) return showToast('Passwords do not match', 'error');
    setPwdSaving(true);
    try {
      await api.users.changePassword(currentUser.id, {
        currentPassword: pwd.current,
        newPassword: pwd.new,
      });
      setPwd({ current: '', new: '', confirm: '' });
      showToast('Password changed successfully');
    } catch (err) {
      showToast(err.message || 'Failed to change password', 'error');
    } finally {
      setPwdSaving(false);
    }
  };

  // ── Change theme (миттєво: застосовується і зберігається одразу, без Save) ──
  const handleThemeChange = next => {
    if (next === theme) return;
    setTheme(next);
    onUserUpdated?.({ ...currentUser, theme: next });
    showToast(next === 'light' ? 'Light theme applied' : 'Dark theme applied');
  };

  // ─── Tabs ─────────────────────────────────────────────────────────────────
  // Preferences тепер керує лише темою інтерфейсу (валюту прибрали за рішенням
  // користувача — ніде фактично не читалась поза цією ж вкладкою) — доступна
  // й адміну, й фрілансеру, обидва можуть обирати dark/light.
  const tabs = [
    { key: 'personal',     label: '👤 Personal Info' },
    ...(isFreelancer ? [{ key: 'fullProfile', label: '📋 My Details & Documents' }] : []),
    { key: 'security',     label: '🔒 Security' },
    { key: 'preferences',  label: '⚙️ Preferences' },
  ];

  // Повний профіль фрілансера (паспорт/документи/банки/тощо) — окрема повносторінкова
  // форма, той самий компонент, що й онбординг, у режимі "edit" (без гейтингу).
  if (tab === 'fullProfile' && isFreelancer) {
    if (!myFreelancer) {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontFamily: 'Arial, sans-serif' }}>
          Loading…
        </div>
      );
    }
    return (
      <OnboardingPage
        mode="edit"
        freelancer={myFreelancer}
        onComplete={setMyFreelancer}
        onBack={() => setTab('personal')}
      />
    );
  }

  const tabStyle = (key) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
    border: 'none', background: 'none', outline: 'none',
    color: tab === key ? 'var(--accent)' : 'var(--text-faint)',
    borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
    transition: 'color 0.15s',
  });

  const btnStyle = (saving) => ({
    background: saving ? 'var(--border-strong)' : 'var(--accent)',
    color: 'var(--text-primary)', border: 'none', borderRadius: '6px',
    padding: '10px 28px', fontSize: '14px', fontWeight: '600',
    cursor: saving ? 'not-allowed' : 'pointer',
  });

  // ── Avatar initials ───────────────────────────────────────────────────────
  const initials = (currentUser?.name || 'U')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Navbar */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px', padding: 0, lineHeight: 1 }}>
          ←
        </button>
        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>Profile</span>
      </div>

      <div style={{ maxWidth: '640px', margin: '40px auto', padding: '0 24px' }}>

        {/* Avatar block */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '36px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600' }}>{currentUser?.name || '—'}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '13px', marginTop: '2px' }}>{currentUser?.email}</div>
            <div style={{ marginTop: '4px' }}>
              <span style={{
                background: ['Admin', 'SuperAdmin'].includes(currentUser?.role) ? 'var(--accent)' : 'var(--border-strong)',
                color: 'var(--text-primary)', fontSize: '10px', padding: '2px 8px',
                borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '1px',
              }}>
                {currentUser?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '32px' }}>
          {tabs.map(t => (
            <button key={t.key} style={tabStyle(t.key)} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Personal Info ──────────────────────────────────────────────── */}
        {tab === 'personal' && (
          <div>
            <Field label="Full Name">
              <input
                style={inputStyle}
                value={personal.name}
                onChange={e => setPersonal(p => ({ ...p, name: e.target.value }))}
                placeholder="Your full name"
              />
            </Field>

            <Field label="Email">
              <input style={inputDisabledStyle} value={currentUser?.email || ''} readOnly />
            </Field>

            <Field label="Phone">
              <input
                style={inputStyle}
                value={personal.phone}
                onChange={e => setPersonal(p => ({ ...p, phone: e.target.value }))}
                placeholder="+1 234 567 8900"
              />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Field label="Country">
                <select
                  style={{ ...inputStyle, appearance: 'none' }}
                  value={personal.country}
                  onChange={e => setPersonal(p => ({ ...p, country: e.target.value }))}>
                  <option value="">— Select country —</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="City">
                <input
                  style={inputStyle}
                  value={personal.city}
                  onChange={e => setPersonal(p => ({ ...p, city: e.target.value }))}
                  placeholder="City"
                />
              </Field>
            </div>

            <button style={btnStyle(personalSaving)} onClick={savePersonal} disabled={personalSaving}>
              {personalSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* ── Security ──────────────────────────────────────────────────── */}
        {tab === 'security' && (
          <div>
            <Field label="Current Password">
              <input
                type="password"
                style={inputStyle}
                value={pwd.current}
                onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password"
              />
            </Field>

            <Field label="New Password">
              <input
                type="password"
                style={inputStyle}
                value={pwd.new}
                onChange={e => setPwd(p => ({ ...p, new: e.target.value }))}
                placeholder="At least 8 characters"
              />
            </Field>

            <Field label="Confirm New Password">
              <input
                type="password"
                style={inputStyle}
                value={pwd.confirm}
                onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Repeat new password"
              />
            </Field>

            {pwd.new && pwd.confirm && pwd.new !== pwd.confirm && (
              <div style={{ color: 'var(--accent)', fontSize: '12px', marginBottom: '16px', marginTop: '-10px' }}>
                Passwords do not match
              </div>
            )}

            <button style={btnStyle(pwdSaving)} onClick={savePassword} disabled={pwdSaving}>
              {pwdSaving ? 'Changing…' : 'Change Password'}
            </button>
          </div>
        )}

        {/* ── Preferences — тема інтерфейсу, доступна і адміну, і фрілансеру ── */}
        {tab === 'preferences' && (
          <div>
            <Field label="Theme">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '4px' }}>
                {[
                  { key: 'dark',  label: '🌙 Dark' },
                  { key: 'light', label: '☀️ Light' },
                ].map(t => (
                  <div
                    key={t.key}
                    onClick={() => handleThemeChange(t.key)}
                    style={{
                      padding: '16px', borderRadius: '8px', textAlign: 'center',
                      cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                      border: `2px solid ${theme === t.key ? 'var(--accent)' : 'var(--border)'}`,
                      background: theme === t.key ? 'var(--danger-bg)' : 'var(--bg-input)',
                      color: theme === t.key ? 'var(--accent)' : 'var(--text-muted)',
                      transition: 'all 0.15s',
                    }}>
                    {t.label}
                  </div>
                ))}
              </div>
              <p style={{ color: 'var(--text-dim)', fontSize: '11px', marginTop: '10px' }}>
                Applies immediately and is remembered on this device — and follows your account when you sign in elsewhere.
              </p>
            </Field>
          </div>
        )}
      </div>

      <Toast msg={toast.msg} type={toast.type} />
    </div>
  );
}
